import React, { useState } from "react";
import { C } from "../theme";
import { submitSupportTicket } from "../services/support";
import Button from "./Button";
import { buildSupportMailto, MailIcon } from "./SupportButton";

/**
 * In-app "Contact Support" sheet. Lets the angler describe the problem, review
 * an auto-captured screenshot, and send a support ticket (screenshot + app
 * diagnostics) without leaving the app. Falls back to a `mailto:` link if the
 * ticket submission fails or the user prefers email.
 *
 * The screenshot is captured by the caller BEFORE this modal mounts, so the
 * modal itself never appears in the image.
 */
export default function SupportModal({
  open,
  onClose,
  screenshotDataUrl = null,
  context = "",
  sessionId = "",
  userId = "",
}) {
  const [description, setDescription] = useState("");
  const [includeShot, setIncludeShot] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  React.useEffect(() => {
    if (open) {
      setDescription("");
      setIncludeShot(true);
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open]);

  if (!open) return null;

  const sending = status === "sending";
  const mailtoHref = buildSupportMailto({ context, userId, sessionId });

  const handleSend = async () => {
    setStatus("sending");
    setErrorMsg("");
    try {
      await submitSupportTicket({
        description,
        context,
        sessionId,
        screenshotDataUrl: includeShot ? screenshotDataUrl : null,
      });
      setStatus("success");
    } catch (err) {
      console.warn("[support] ticket submit failed:", err?.message || err);
      setErrorMsg(err?.message || "Something went wrong sending your request.");
      setStatus("error");
    }
  };

  const overlayStyle = {
    position: "absolute",
    inset: 0,
    background: "rgba(4,8,15,0.72)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    zIndex: 12000,
    padding: 0,
  };

  const sheetStyle = {
    width: "100%",
    maxHeight: "92%",
    overflowY: "auto",
    background: C.cardBg,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTop: `1px solid ${C.border}`,
    boxShadow: "0 -10px 40px rgba(0,0,0,0.5)",
    padding: "18px 18px calc(20px + env(safe-area-inset-bottom, 0px))",
    color: C.white,
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: C.muted,
    margin: "0 0 6px",
  };

  return (
    <div style={overlayStyle} onClick={sending ? undefined : onClose}>
      <div
        style={sheetStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Contact support"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span aria-hidden style={{ display: "inline-flex", color: C.accent }}>
            <MailIcon size={18} color={C.accent} />
          </span>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Contact Support</h2>
        </div>

        {status === "success" ? (
          <div style={{ padding: "8px 0 4px" }}>
            <p style={{ margin: "10px 0", fontSize: 14, lineHeight: 1.5 }}>
              Thanks — your request is on its way to our team. We’ll get back to you by
              email{userId ? "" : " soon"}.
            </p>
            <Button variant="primary" onClick={onClose} style={{ flex: 1 }}>
              Done
            </Button>
          </div>
        ) : (
          <>
            <p style={{ margin: "4px 0 14px", fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
              Tell us what went wrong and we’ll take a look. App details and a screenshot
              are included automatically to help us help you faster.
            </p>

            <p style={labelStyle}>What happened?</p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the problem you ran into…"
              rows={4}
              disabled={sending}
              style={{
                width: "100%",
                boxSizing: "border-box",
                resize: "vertical",
                minHeight: 88,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                background: C.deepWater,
                color: C.white,
                fontSize: 14,
                fontFamily: "inherit",
                outline: "none",
                marginBottom: 16,
              }}
            />

            {screenshotDataUrl ? (
              <div style={{ marginBottom: 16 }}>
                <p style={labelStyle}>Screenshot</p>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  <img
                    src={screenshotDataUrl}
                    alt="Screen capture preview"
                    style={{
                      width: 76,
                      height: 76,
                      objectFit: "cover",
                      objectPosition: "top",
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      opacity: includeShot ? 1 : 0.35,
                      flexShrink: 0,
                    }}
                  />
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      color: C.sand,
                      cursor: "pointer",
                      paddingTop: 4,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={includeShot}
                      onChange={(e) => setIncludeShot(e.target.checked)}
                      disabled={sending}
                      style={{ width: 18, height: 18, accentColor: C.accent }}
                    />
                    Attach this screenshot
                  </label>
                </div>
              </div>
            ) : null}

            {status === "error" ? (
              <div
                style={{
                  background: `${C.red}22`,
                  border: `1px solid ${C.red}66`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  marginBottom: 14,
                  fontSize: 13,
                  color: C.white,
                }}
              >
                <div style={{ marginBottom: 8 }}>{errorMsg}</div>
                <a
                  href={mailtoHref}
                  style={{
                    color: C.gold,
                    fontWeight: 700,
                    textDecoration: "underline",
                    fontSize: 13,
                  }}
                >
                  Email us instead
                </a>
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={sending}
                style={{ flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSend}
                disabled={sending || description.trim().length === 0}
                style={{ flex: 1 }}
              >
                {sending ? "Sending…" : "Send"}
              </Button>
            </div>

            <div style={{ textAlign: "center", marginTop: 12 }}>
              <a
                href={mailtoHref}
                style={{ color: C.muted, fontSize: 12, textDecoration: "underline" }}
              >
                Prefer email? Contact us directly
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


